/**
 * /music-stand — the page: library, setlists, the reader's panels.
 * The stand's logic is in core.js / gesture-core.js (tested in node), the
 * drawing in reader.js, the camera in gestures.js.
 */
import { openStore, askPersist, newId } from './store.js';
import { splitFiles, prepareImage, thumbOf, describePdf } from './source.js';
import { pieceName, DEFAULT_KEYS, keyId } from './core.js';
import { createReader } from './reader.js';
import { createGestures } from './gestures.js';
import { demoPages } from './demo.js';

const CFG = window.__MS_CFG || {};
const SLUG = 'music-stand';
const $ = (id) => document.getElementById(id);

function t(key, repl) {
    let s = (CFG.t && CFG.t[key]) || key;
    if (repl) for (const k of Object.keys(repl)) s = s.split(':' + k).join(String(repl[k]));
    return s;
}
const toast = (m, type) => { try { window.__toast ? window.__toast(m, type || 'info') : console.log(m); } catch (_) { /* no toast */ } };
function diag(issue, err, extra) {
    try {
        window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({
            issue, msg: String((err && (err.name + ': ' + err.message)) || err || '').slice(0, 140),
        }, extra || {}));
    } catch (_) { /* optional */ }
}
const stage = (n, m) => { try { window.__stage && window.__stage(n, m || {}); } catch (_) { /* optional */ } };

/* ── settings (remembered on this device) ─────────────────────────────── */

const TS = window.__toolSettings;
const DEF = {
    mode: 'auto', half: false, halfStyle: 'keep', crop: true, theme: 'paper', bright: 100, anim: 'fade',
    spreadStep: 2, keys: null, gap: 250, gKind: 'head', gSens: 5, gCal: {}, pageNum: true, hintSeen: false,
};
const settings = {};
for (const k of Object.keys(DEF)) settings[k] = TS ? TS.load(SLUG, k, DEF[k]) : DEF[k];
function saveSetting(k, v) {
    settings[k] = v;
    try { TS && TS.save(SLUG, k, v); } catch (_) { /* storage off */ }
}

// pedal learning: 'next' | 'prev' | null (the pedal panel)
let learning = null;

const reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/* ── elements ─────────────────────────────────────────────────────────── */

const els = {
    stage: $('msStage'), sheet: $('msSheet'), page: $('msPage'), ink: $('msInk'), fx: $('msFx'),
    chips: $('msChips'), banner: $('msBanner'), msg: $('msMsg'), edgeL: $('msEdgeL'), edgeR: $('msEdgeR'),
    gPanel: $('msPanelHands'), gStatus: $('gStatus'), gOn: $('gOn'), gKind: $('gKind'), gSens: $('gSens'),
    gCalib: $('gCalib'), gCalMsg: $('gCalMsg'), gPreview: $('gPreview'), gMeter: $('gMeter'), gCount: $('gCount'),
    gFail: $('gFail'), gVideo: $('gVideo'), gDot: $('gDot'),
};

const S = {
    cfg: CFG, t, locale: CFG.locale || window.__LOCALE || 'en', settings, saveSetting, els, reduced, diag,
    store: null,
};

/* ── library ──────────────────────────────────────────────────────────── */

let pieces = [];
let setlists = [];
const thumbUrls = new Map();

function relTime(ts) {
    if (!ts) return '';
    const d = (Date.now() - ts) / 1000;
    try {
        const rtf = new Intl.RelativeTimeFormat(S.locale, { numeric: 'auto' });
        if (d < 3600) return rtf.format(-Math.max(1, Math.round(d / 60)), 'minute');
        if (d < 86400) return rtf.format(-Math.round(d / 3600), 'hour');
        return rtf.format(-Math.round(d / 86400), 'day');
    } catch (_) { return new Date(ts).toLocaleDateString(); }
}

function pagesText(n) {
    try {
        const rule = new Intl.PluralRules(S.locale).select(n);
        return t('pages_' + rule, { n }) !== 'pages_' + rule ? t('pages_' + rule, { n }) : t('pages_other', { n });
    } catch (_) { return t('pages_other', { n }); }
}

function btn(label, cls, attrs) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls || 'lg-btn';
    b.textContent = label;
    if (attrs) for (const k of Object.keys(attrs)) b.setAttribute(k, attrs[k]);
    return b;
}

async function loadLibrary() {
    pieces = (await S.store.all('pieces').catch(() => [])) || [];
    pieces.sort((a, b) => (b.opened || b.created || 0) - (a.opened || a.created || 0));
    setlists = (await S.store.all('setlists').catch(() => [])) || [];
    setlists.sort((a, b) => (a.created || 0) - (b.created || 0));
    renderPieces();
    renderSetlists();
}

function thumbUrl(p) {
    if (!p.thumb) return '';
    if (!thumbUrls.has(p.id)) thumbUrls.set(p.id, URL.createObjectURL(p.thumb));
    return thumbUrls.get(p.id);
}

function renderPieces() {
    const box = $('msPieces');
    box.textContent = '';
    $('msEmpty').hidden = pieces.length > 0;
    for (const p of pieces) {
        const row = document.createElement('div');
        row.className = 'ms-piece';
        row.dataset.id = p.id;
        const img = document.createElement(p.thumb ? 'img' : 'div');
        img.className = 'ms-thumb';
        if (p.thumb) { img.src = thumbUrl(p); img.alt = ''; }
        const info = document.createElement('div');
        info.className = 'ms-pinfo';
        const nm = document.createElement('div');
        nm.className = 'ms-pname';
        nm.textContent = p.name;
        const meta = document.createElement('div');
        meta.className = 'ms-pmeta';
        const bits = [pagesText(p.pages)];
        if (p.lastPage) bits.push(t('stopped_at', { page: p.lastPage + 1 }));
        if (p.opened) bits.push(relTime(p.opened));
        meta.textContent = bits.join(' · ');
        info.append(nm, meta);
        const acts = document.createElement('div');
        acts.className = 'ms-pacts';
        const open = btn(t('open'), 'lg-btn lg-btn-primary ms-open', { 'data-act': 'open' });
        const more = btn('⋯', 'lg-btn ms-more', { 'data-act': 'more', 'aria-expanded': 'false', 'aria-label': t('more_actions') });
        acts.append(open, more);
        const extra = document.createElement('div');
        extra.className = 'ms-pmore';
        extra.hidden = true;
        const ren = document.createElement('input');
        ren.type = 'text'; ren.className = 'ms-in'; ren.value = p.name; ren.maxLength = 120;
        ren.setAttribute('aria-label', t('rename'));
        const renB = btn(t('rename'), 'lg-btn', { 'data-act': 'rename' });
        const del = btn(t('delete'), 'lg-btn ms-danger', { 'data-act': 'delete' });
        const sets = document.createElement('select');
        sets.className = 'ms-in';
        sets.setAttribute('aria-label', t('add_to_setlist'));
        sets.append(new Option(t('add_to_setlist'), ''));
        for (const s of setlists) sets.append(new Option(s.name, s.id));
        sets.addEventListener('change', async () => {
            const s = setlists.find((x) => x.id === sets.value);
            if (!s) return;
            s.items = (s.items || []).concat(p.id);
            await S.store.put('setlists', s);
            toast(t('added_to_setlist', { name: s.name }), 'success');
            sets.value = '';
            renderSetlists();
        });
        const r1 = document.createElement('div'); r1.className = 'ms-row'; r1.append(ren, renB);
        const r2 = document.createElement('div'); r2.className = 'ms-row';
        if (setlists.length) r2.append(sets);
        r2.append(del);
        extra.append(r1, r2);
        row.append(img, info, acts, extra);
        open.addEventListener('click', () => openPiece(p.id));
        more.addEventListener('click', () => {
            extra.hidden = !extra.hidden;
            more.setAttribute('aria-expanded', extra.hidden ? 'false' : 'true');
        });
        renB.addEventListener('click', async () => {
            const v = ren.value.trim();
            if (!v) return;
            p.name = v;
            await S.store.put('pieces', p);
            renderPieces(); renderSetlists();
        });
        ren.addEventListener('keydown', (e) => { if (e.key === 'Enter') renB.click(); });
        del.addEventListener('click', async () => {
            if (del.dataset.armed !== '1') {
                del.dataset.armed = '1';
                del.textContent = t('delete_confirm');
                setTimeout(() => { del.dataset.armed = ''; del.textContent = t('delete'); }, 4000);
                return;
            }
            await S.store.delPiece(p.id);
            for (const s of setlists) {
                if ((s.items || []).includes(p.id)) { s.items = s.items.filter((x) => x !== p.id); await S.store.put('setlists', s); }
            }
            if (thumbUrls.has(p.id)) { URL.revokeObjectURL(thumbUrls.get(p.id)); thumbUrls.delete(p.id); }
            await loadLibrary();
        });
        box.appendChild(row);
    }
}

function renderSetlists() {
    const box = $('msSets');
    box.textContent = '';
    $('msSetsEmpty').hidden = setlists.length > 0;
    const byId = new Map(pieces.map((p) => [p.id, p]));
    for (const s of setlists) {
        const card = document.createElement('div');
        card.className = 'ms-set';
        const head = document.createElement('div');
        head.className = 'ms-set-head';
        const nm = document.createElement('div');
        nm.className = 'ms-set-name';
        const items = (s.items || []).filter((id) => byId.has(id));
        nm.textContent = s.name;
        const cnt = document.createElement('span');
        cnt.className = 'ms-pmeta';
        cnt.textContent = t('n_pieces', { n: items.length });
        nm.appendChild(document.createTextNode(' '));
        nm.appendChild(cnt);
        const play = btn('▶ ' + t('play_setlist'), 'lg-btn lg-btn-primary');
        play.disabled = !items.length;
        const del = btn(t('delete'), 'lg-btn ms-danger');
        head.append(nm, play, del);
        const ol = document.createElement('ol');
        ol.className = 'ms-set-items';
        items.forEach((id, idx) => {
            const li = document.createElement('li');
            const sp = document.createElement('span');
            sp.textContent = byId.get(id).name;
            const up = btn('↑', 'ms-mini', { 'aria-label': t('move_up') });
            const dn = btn('↓', 'ms-mini', { 'aria-label': t('move_down') });
            const rm = btn('✕', 'ms-mini', { 'aria-label': t('remove') });
            up.disabled = idx === 0; dn.disabled = idx === items.length - 1;
            const move = async (d) => {
                const a = items.slice();
                const j = idx + d;
                [a[idx], a[j]] = [a[j], a[idx]];
                s.items = a;
                await S.store.put('setlists', s);
                renderSetlists();
            };
            up.addEventListener('click', () => move(-1));
            dn.addEventListener('click', () => move(1));
            rm.addEventListener('click', async () => {
                s.items = items.filter((_, k) => k !== idx);
                await S.store.put('setlists', s);
                renderSetlists();
            });
            li.append(sp, up, dn, rm);
            ol.appendChild(li);
        });
        const add = document.createElement('div');
        add.className = 'ms-row';
        const sel = document.createElement('select');
        sel.className = 'ms-in';
        sel.setAttribute('aria-label', t('add_piece'));
        sel.append(new Option(t('add_piece'), ''));
        for (const p of pieces) sel.append(new Option(p.name, p.id));
        sel.addEventListener('change', async () => {
            if (!sel.value) return;
            s.items = items.concat(sel.value);
            await S.store.put('setlists', s);
            renderSetlists();
        });
        add.appendChild(sel);
        card.append(head, ol, add);
        play.addEventListener('click', () => openSetlist(s.id));
        del.addEventListener('click', async () => {
            if (del.dataset.armed !== '1') {
                del.dataset.armed = '1'; del.textContent = t('delete_confirm');
                setTimeout(() => { del.dataset.armed = ''; del.textContent = t('delete'); }, 4000);
                return;
            }
            await S.store.del('setlists', s.id);
            await loadLibrary();
        });
        box.appendChild(card);
    }
}

$('msSetNew').addEventListener('click', async () => {
    const inp = $('msSetName');
    const name = inp.value.trim() || t('setlist_default', { n: setlists.length + 1 });
    const s = { id: newId(), name, items: [], created: Date.now() };
    await S.store.put('setlists', s);
    inp.value = '';
    stage('setlist_created');
    await loadLibrary();
});
$('msSetName').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('msSetNew').click(); });

/* ── import ───────────────────────────────────────────────────────────── */

async function importFiles(list) {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    const { pdfs, images, other } = splitFiles(files);
    if (other.length) toast(t('err_type', { name: other[0].name }), 'warning');
    const box = $('msImport');
    const say = (m) => { box.hidden = !m; box.textContent = m || ''; };
    const added = [];
    let n = 0;
    const total = pdfs.length + (images.length ? 1 : 0);
    try {
        for (const f of pdfs) {
            n++;
            say(t('importing', { name: f.name, i: n, n: total }));
            try {
                const d = await describePdf(f, CFG);
                const rec = { id: newId(), name: pieceName([f.name]) || t('untitled'), kind: 'pdf', pages: d.pages, files: 1, bytes: f.size, created: Date.now(), opened: 0, lastPage: 0, links: [], marks: [], thumb: d.thumb };
                await S.store.addPiece(rec, [{ blob: f, name: f.name }]);
                added.push(rec);
            } catch (e) {
                toast((e && /Password/i.test(e.name)) ? t('err_pdf_password') : t('err_pdf', { name: f.name }), 'error');
                diag('import_pdf_failed', e);
            }
        }
        if (images.length) {
            n++;
            const prepared = [];
            for (let k = 0; k < images.length; k++) {
                say(t('importing_photos', { i: k + 1, n: images.length }));
                try { prepared.push(Object.assign(await prepareImage(images[k]), { name: images[k].name })); }
                catch (e) { toast(t('err_image', { name: images[k].name }), 'error'); diag('import_image_failed', e, { type: images[k].type }); }
            }
            if (prepared.length) {
                const firstBmp = await createImageBitmap(prepared[0].blob).catch(() => null);
                const thumb = firstBmp ? await thumbOf(firstBmp, prepared[0].w, prepared[0].h).catch(() => null) : null;
                const rec = { id: newId(), name: pieceName(prepared.map((x) => x.name)) || t('untitled'), kind: 'images', pages: prepared.length, files: prepared.length, bytes: prepared.reduce((a, x) => a + x.blob.size, 0), created: Date.now(), opened: 0, lastPage: 0, links: [], marks: [], thumb };
                await S.store.addPiece(rec, prepared.map((x) => ({ blob: x.blob, name: x.name })));
                added.push(rec);
            }
        }
    } catch (e) {
        const quota = e && /Quota/i.test(e.name || '');
        toast(quota ? t('err_quota') : t('err_save'), 'error');
        diag(quota ? 'import_quota' : 'import_failed', e);
    } finally {
        say('');
    }
    if (!added.length) return;
    stage('imported', { pieces: added.length, kind: added[0].kind, pages: added.reduce((a, r) => a + r.pages, 0) });
    askPersist().then((ok) => { if (!ok && S.store.persistent) $('msPersistNote').hidden = false; });
    await loadLibrary();
    // one piece opens at once — that IS the confirmation; a toast would land on
    // the reader's top bar. Several: the first opens, the banner says how many.
    await openPiece(added[0].id);
    if (added.length > 1) R.banner(t('added_many', { n: added.length }), 2600);
}

// Race-safe: a file picked before this module ran (slow network, blockers)
// is replayed by the shared binder instead of being lost.
const drop = $('msDrop');
const onPicked = (file, d) => importFiles(d && d.files && d.files.length ? d.files : [file]);
// (the binder ships with the uploader itself: without it there is no zone to pick from)
if (drop && typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(drop, onPicked);

$('msDemo').addEventListener('click', async () => {
    const b = $('msDemo');
    b.disabled = true;
    try {
        const pages = await demoPages(t('demo_title'), { ds: t('demo_ds'), toCoda: t('demo_to_coda'), coda: t('demo_coda') });
        const bmp = await createImageBitmap(pages[0].blob);
        const thumb = await thumbOf(bmp, pages[0].w, pages[0].h);
        const rec = {
            id: newId(), name: t('demo_title'), kind: 'images', pages: 4, files: 4, bytes: pages.reduce((a, x) => a + x.blob.size, 0),
            created: Date.now(), opened: 0, lastPage: 0, thumb, demo: true, marks: [],
            links: [
                { id: newId(), from: 2, to: 0, label: t('demo_ds'), pass: 1, x: 0.8, y: 0.905 },
                { id: newId(), from: 1, to: 3, label: t('demo_to_coda'), pass: 2, x: 0.8, y: 0.36 },
            ],
        };
        await S.store.addPiece(rec, pages.map((x) => ({ blob: x.blob, name: x.name })));
        stage('demo_added');
        await loadLibrary();
        openPiece(rec.id);
    } catch (e) {
        toast(t('err_save'), 'error');
        diag('demo_failed', e);
    } finally { b.disabled = false; }
});

/* ── the reader ───────────────────────────────────────────────────────── */

const R = createReader(S);
const G = createGestures(S, R);

function showReader(on) {
    $('msLib').hidden = on;
    $('msReader').hidden = !on;
}

async function openPiece(id) {
    const rec = pieces.find((p) => p.id === id) || await S.store.get('pieces', id);
    if (!rec) return;
    showReader(true);
    applyLook();
    try { window.__wakeLock && window.__wakeLock.request(); } catch (_) { /* no wake lock */ }
    await R.openSession([rec], { page: rec.lastPage || 0 });
    rec.opened = Date.now();
    S.store.put('pieces', rec).catch(() => {});
    stage('reading', { kind: rec.kind, pages: rec.pages, setlist: 0 });
    if (rec.lastPage > 0) R.banner(t('resumed', { page: rec.lastPage + 1 }), 2600);
    afterOpen();
}

async function openSetlist(id) {
    const s = setlists.find((x) => x.id === id);
    if (!s) return;
    const recs = (s.items || []).map((pid) => pieces.find((p) => p.id === pid)).filter(Boolean);
    if (!recs.length) return;
    showReader(true);
    applyLook();
    try { window.__wakeLock && window.__wakeLock.request(); } catch (_) { /* no wake lock */ }
    await R.openSession(recs, { setlist: s.id });
    stage('reading', { setlist: 1, pieces: recs.length });
    R.banner(`1/${recs.length} · ${recs[0].name}`, 2600);
    afterOpen();
}

function afterOpen() {
    updateTop();
    refreshMarks();
    if (!settings.hintSeen) {
        const h = $('msHint');
        h.hidden = false;
        setTimeout(() => { h.hidden = true; }, 4200);
        saveSetting('hintSeen', true);
    }
}

function closeReader() {
    closePanels();
    if (G.on) G.disable();
    R.close();
    try { window.__wakeLock && window.__wakeLock.release(); } catch (_) { /* no wake lock */ }
    showReader(false);
    loadLibrary();
}
S.onClose = () => {};
$('msBack').addEventListener('click', closeReader);

function updateTop() {
    const c = R.current();
    if (!c) return;
    const v = c.view;
    let pos;
    if (v.kind === 'half') pos = t('pos_half', { a: c.p + 1, b: R.seq[v.j].p + 1, n: c.pages });
    else if (v.kind === 'spread' && v.j != null) pos = t('pos_spread', { a: c.p + 1, b: c.p + 2, n: c.pages });
    else if (v.kind === 'seg' && v.parts > 1) pos = t('pos_seg', { p: c.p + 1, n: c.pages, s: v.part + 1, m: v.parts });
    else pos = t('pos_page', { p: c.p + 1, n: c.pages });
    const setPos = R.pieces.length > 1 ? ` · ${t('pos_setlist', { k: c.k + 1, n: R.pieces.length })}` : '';
    $('msTitle').textContent = c.piece.name;
    $('msPos').textContent = pos + setPos;
    $('msPTitle').textContent = c.piece.name + ' · ' + pos;
    const cn = $('msCornerNum');
    cn.textContent = v.kind === 'half' ? `${c.p + 1}→${R.seq[v.j].p + 1}` : String(c.p + 1);
    cn.hidden = !settings.pageNum;
    $('msPrev').disabled = c.i === 0 && c.view.kind !== 'half' && !(c.view.kind === 'seg' && c.view.part > 0);
}
S.onRendered = updateTop;
S.onTurn = () => { refreshGoto(); };

/* ── the toolbar and its panels ───────────────────────────────────────── */

const panels = { view: $('msPanelView'), ink: $('msPanelInk'), marks: $('msPanelMarks'), hands: $('msPanelHands'), pedal: $('msPanelPedal') };
function closePanels() {
    for (const k of Object.keys(panels)) panels[k].hidden = true;
    document.querySelectorAll('[data-panel]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (R.mode !== 'read') R.setMode('read');
    learning = null;
}
document.querySelectorAll('[data-panel]').forEach((b) => {
    b.addEventListener('click', () => {
        const k = b.dataset.panel;
        const wasOpen = !panels[k].hidden;
        closePanels();
        if (wasOpen) return;
        panels[k].hidden = false;
        b.setAttribute('aria-expanded', 'true');
        if (k === 'ink') { R.setMode('ink'); stage('annotate'); }
        if (k === 'marks') refreshMarks();
        if (k === 'hands') G.drawMeter();
        if (k === 'pedal') renderKeys();
        try { panels[k].scrollIntoView({ block: 'nearest' }); } catch (_) { /* old */ }
    });
});

$('msPrev').addEventListener('click', () => R.turn(-1, 'button'));
$('msNext').addEventListener('click', () => R.turn(1, 'button'));
$('msPPrev').addEventListener('click', (e) => { e.stopPropagation(); R.turn(-1, 'button'); });
$('msPNext').addEventListener('click', (e) => { e.stopPropagation(); R.turn(1, 'button'); });
$('msPerf').addEventListener('click', () => { closePanels(); R.enterPerf(); });
$('msPExit').addEventListener('click', (e) => { e.stopPropagation(); R.exitPerf(); });
['msPbar'].forEach((id) => $(id).addEventListener('pointerdown', (e) => e.stopPropagation()));

let barT = 0;
function showBar(ms) {
    const b = $('msPbar');
    b.classList.add('ms-show');
    clearTimeout(barT);
    barT = setTimeout(() => b.classList.remove('ms-show'), ms || 3500);
}
S.onCenterTap = () => { if (R.perf) showBar(4000); };
S.onPerf = (on) => {
    $('msPerf').setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) showBar(3000);
    updateTop();
};

/* ── look: theme and brightness ───────────────────────────────────────── */

function applyLook() {
    const b = Math.min(100, Math.max(30, +settings.bright || 100)) / 100;
    let f = '';
    if (settings.theme === 'night') f = 'invert(1) hue-rotate(180deg)';
    else if (settings.theme === 'sepia') f = 'sepia(.38) saturate(1.1)';
    if (b < 1) f += ` brightness(${b})`;
    els.sheet.style.filter = f.trim();
    els.stage.dataset.theme = settings.theme;
}

function bindSelect(id, key, after, cast) {
    const el = $(id);
    if (!el) return;
    el.value = String(settings[key]);
    el.addEventListener('change', () => {
        const v = cast ? cast(el.value) : el.value;
        saveSetting(key, v);
        if (after) after(v);
    });
}
function bindCheck(id, key, after) {
    const el = $(id);
    el.checked = !!settings[key];
    el.addEventListener('change', () => { saveSetting(key, el.checked); if (after) after(el.checked); });
}

bindSelect('msMode', 'mode', () => { syncViewUi(); R.relayout(); stage('view_mode', { mode: settings.mode }); });
bindSelect('msStep', 'spreadStep', () => R.relayout(), (v) => +v);
bindCheck('msHalf', 'half', () => { syncViewUi(); R.relayout(); stage('half_page', { on: settings.half ? 1 : 0 }); });
bindSelect('msHalfStyle', 'halfStyle', () => R.relayout());
bindCheck('msCrop', 'crop', () => R.relayout());
bindSelect('msTheme', 'theme', applyLook);
bindSelect('msAnim', 'anim');
bindCheck('msPageNum', 'pageNum', updateTop);
{
    const br = $('msBright');
    br.value = settings.bright;
    br.addEventListener('input', () => { saveSetting('bright', +br.value); applyLook(); });
}

function syncViewUi() {
    $('msHalfRow').hidden = !(settings.mode === 'page' || settings.mode === 'auto');
    $('msHalfOpts').hidden = !settings.half;
    $('msStepRow').hidden = !(settings.mode === 'spread' || settings.mode === 'auto');
    const split = $('msSplitBtn');
    split.setAttribute('aria-pressed', R.mode === 'split' ? 'true' : 'false');
}
$('msSplitBtn').addEventListener('click', () => {
    if (R.mode === 'split') { R.setMode('read'); }
    else {
        if (!settings.half) { settings.half = true; $('msHalf').checked = true; saveSetting('half', true); }
        R.setMode('split');
        R.relayout();
        toast(t('split_hint'), 'info');
    }
    syncViewUi();
});
$('msSplitReset').addEventListener('click', () => R.resetSplit());
syncViewUi();

/* ── lock ─────────────────────────────────────────────────────────────── */

function setLock(on) {
    R.setLocked(on);
    for (const id of ['msLock', 'msPLock']) {
        const b = $(id);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.querySelector('.ms-lbl') && (b.querySelector('.ms-lbl').textContent = on ? t('unlock_hold') : t('lock'));
    }
    if (on) { closePanels(); stage('locked'); }
}
function bindLock(id) {
    const b = $(id);
    let holdT = 0, justUnlocked = false;
    b.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        if (!R.locked) return;
        b.classList.add('ms-holding');
        holdT = setTimeout(() => {
            b.classList.remove('ms-holding');
            setLock(false);
            // the click that ends this press must not lock it again
            justUnlocked = true;
            setTimeout(() => { justUnlocked = false; }, 800);
            R.banner(t('unlocked'), 1400);
        }, 900);
    });
    const cancel = () => { clearTimeout(holdT); b.classList.remove('ms-holding'); };
    b.addEventListener('pointerup', cancel);
    b.addEventListener('pointerleave', cancel);
    b.addEventListener('pointercancel', cancel);
    b.addEventListener('click', (e) => {
        e.stopPropagation();
        if (justUnlocked) { justUnlocked = false; return; }
        // said on the stand, not in a toast: a toast at the top of the window
        // lands on the toolbar — right over the button that unlocks
        if (!R.locked) { setLock(true); lockNote(t('locked_note'), 3200); }
    });
    b.addEventListener('keydown', (e) => {
        // keyboard users: Enter toggles both ways (a pedal never focuses this button)
        if (e.key === 'Enter' && R.locked && document.activeElement === b) { e.preventDefault(); setLock(false); }
    });
}
bindLock('msLock');
bindLock('msPLock');
function lockNote(text, ms) {
    const n = $('msLockNote');
    n.textContent = '🔒 ' + text;
    n.hidden = false;
    clearTimeout(n.__t);
    n.__t = setTimeout(() => { n.hidden = true; }, ms || 1600);
}
S.onLockedTouch = () => {
    lockNote(t('locked_touch'), 1600);
    if (R.perf) showBar(3000);
};

/* ── ink panel ────────────────────────────────────────────────────────── */

document.querySelectorAll('[data-tool]').forEach((b) => b.addEventListener('click', () => {
    R.ink.setTool(b.dataset.tool);
    document.querySelectorAll('[data-tool]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
    $('msStamps').hidden = b.dataset.tool !== 'stamp';
}));
document.querySelectorAll('[data-color]').forEach((b) => b.addEventListener('click', () => {
    R.ink.setColor(b.dataset.color);
    document.querySelectorAll('[data-color]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
}));
document.querySelectorAll('[data-hl]').forEach((b) => b.addEventListener('click', () => {
    R.ink.setHl(b.dataset.hl);
    document.querySelectorAll('[data-hl]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
}));
document.querySelectorAll('[data-stamp]').forEach((b) => b.addEventListener('click', () => {
    R.ink.setStamp(b.dataset.stamp);
    $('msStampText').value = b.dataset.stamp;
    document.querySelectorAll('[data-stamp]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
}));
$('msStampText').addEventListener('input', (e) => { const v = e.target.value.trim().slice(0, 12); if (v) R.ink.setStamp(v); });
$('msUndo').addEventListener('click', () => { if (!R.ink.undo()) toast(t('nothing_to_undo'), 'info'); });
$('msClear').addEventListener('click', () => {
    const b = $('msClear');
    if (b.dataset.armed !== '1') {
        b.dataset.armed = '1'; b.textContent = t('clear_confirm');
        setTimeout(() => { b.dataset.armed = ''; b.textContent = t('clear_page'); }, 4000);
        return;
    }
    b.dataset.armed = ''; b.textContent = t('clear_page');
    for (const tile of R.tiles) R.ink.clearPage(tile.i);
});
$('msInkDone').addEventListener('click', closePanels);

/* ── marks & jumps ────────────────────────────────────────────────────── */

function curPiece() { const c = R.current(); return c ? R.pieces[c.k] : null; }
function savePieceRec(P) { return S.store.put('pieces', P.rec).catch((e) => diag('piece_save_failed', e)); }

function refreshGoto() {
    const sel = $('msGoto');
    const c = R.current();
    if (!c || !sel) return;
    const P = R.pieces[c.k];
    const want = String(c.p);
    if (sel.dataset.piece !== P.rec.id || sel.options.length !== P.rec.pages + (P.rec.marks || []).length) {
        sel.textContent = '';
        for (const m of P.rec.marks || []) sel.append(new Option(`🔖 ${m.name} · ${m.page + 1}`, 'm' + m.page));
        for (let p = 0; p < P.rec.pages; p++) sel.append(new Option(t('page_n', { n: p + 1 }), String(p)));
        sel.dataset.piece = P.rec.id;
    }
    sel.value = want;
}

function refreshMarks() {
    const P = curPiece();
    if (!P) return;
    refreshGoto();
    const to = $('msJumpTo');
    to.textContent = '';
    for (const m of P.rec.marks || []) to.append(new Option(`🔖 ${m.name} · ${m.page + 1}`, String(m.page)));
    for (let p = 0; p < P.rec.pages; p++) to.append(new Option(t('page_n', { n: p + 1 }), String(p)));
    const ml = $('msMarks');
    ml.textContent = '';
    for (const m of P.rec.marks || []) {
        const li = document.createElement('li');
        const go = btn(`🔖 ${m.name} · ${m.page + 1}`, 'ms-linkbtn');
        go.addEventListener('click', () => { R.goTo(R.starts[R.current().k] + m.page); });
        const rm = btn('✕', 'ms-mini', { 'aria-label': t('remove') });
        rm.addEventListener('click', async () => { P.rec.marks = P.rec.marks.filter((x) => x !== m); await savePieceRec(P); refreshMarks(); });
        li.append(go, rm);
        ml.appendChild(li);
    }
    $('msMarksEmpty').hidden = !!(P.rec.marks || []).length;
    const jl = $('msJumps');
    jl.textContent = '';
    for (const l of P.rec.links || []) {
        const li = document.createElement('li');
        const s = document.createElement('span');
        s.textContent = t('jump_row', { label: l.label || '↪', from: l.from + 1, to: l.to + 1 }) + ' · ' + (l.pass ? t('pass_n', { n: l.pass }) : t('pass_tap'));
        const rm = btn('✕', 'ms-mini', { 'aria-label': t('remove') });
        rm.addEventListener('click', async () => { P.rec.links = P.rec.links.filter((x) => x !== l); await savePieceRec(P); refreshMarks(); R.render(); });
        li.append(s, rm);
        jl.appendChild(li);
    }
    $('msJumpsEmpty').hidden = !!(P.rec.links || []).length;
}

$('msGotoBtn').addEventListener('click', () => {
    const c = R.current();
    if (!c) return;
    const v = $('msGoto').value;
    const p = v.startsWith('m') ? +v.slice(1) : +v;
    R.goTo(R.starts[c.k] + p);
});
$('msMarkAdd').addEventListener('click', async () => {
    const P = curPiece();
    const c = R.current();
    if (!P || !c) return;
    const name = $('msMarkName').value.trim() || t('page_n', { n: c.p + 1 });
    P.rec.marks = (P.rec.marks || []).concat({ id: newId(), name: name.slice(0, 40), page: c.p });
    $('msMarkName').value = '';
    await savePieceRec(P);
    refreshMarks();
    toast(t('mark_added', { name }), 'success');
});
let pendingJump = null;
$('msJumpAdd').addEventListener('click', () => {
    const c = R.current();
    if (!c) return;
    pendingJump = {
        id: newId(), from: c.p, to: +$('msJumpTo').value || 0,
        label: ($('msJumpLabel').value || '').trim().slice(0, 24) || '↪',
        pass: +$('msJumpPass').value || 0,
    };
    R.setMode('place');
    $('msPlaceNote').hidden = false;
});
$('msPlaceCancel').addEventListener('click', () => { pendingJump = null; R.setMode('read'); $('msPlaceNote').hidden = true; });
S.onPlace = async (q) => {
    if (!pendingJump) return;
    const e = R.seq[q.i];
    const P = R.pieces[e.k];
    const l = Object.assign(pendingJump, { from: e.p, x: Math.round(q.u * 1e4) / 1e4, y: Math.round(q.v * 1e4) / 1e4 });
    pendingJump = null;
    P.rec.links = (P.rec.links || []).concat(l);
    await savePieceRec(P);
    R.setMode('read');
    $('msPlaceNote').hidden = true;
    refreshMarks();
    R.render();
    toast(t('jump_added'), 'success');
    stage('jump_added', { pass: l.pass });
};
$('msRestart').addEventListener('click', () => { R.restart(); toast(t('restarted'), 'info'); });

/* ── pedal panel ──────────────────────────────────────────────────────── */

const KEY_NAMES = { ArrowRight: '→', ArrowLeft: '←', ArrowUp: '↑', ArrowDown: '↓', PageDown: 'Page Down', PageUp: 'Page Up', Space: 'Space', 'Shift+Space': 'Shift+Space', Enter: 'Enter', NumpadEnter: 'Enter (num)', Backspace: 'Backspace' };
const keyName = (k) => KEY_NAMES[k] || k.replace(/^Key/, '').replace(/^Digit/, '');
function renderKeys() {
    const m = R.keysMap();
    $('msKeysNext').textContent = m.next.map(keyName).join(', ');
    $('msKeysPrev').textContent = m.prev.map(keyName).join(', ');
}
S.keyTap = (e) => {
    if (!panels.pedal.hidden) {
        const id = keyId(e);
        if (!/^(Shift|Control|Alt|Meta)(Left|Right)?$/.test(id)) $('msKeyLast').textContent = t('key_seen', { key: keyName(id) });
    }
    if (!learning) return false;
    const id = keyId(e);
    if (/^(Shift|Control|Alt|Meta)(Left|Right)?$/.test(id) || id === 'Escape' || id === 'Tab') return false;
    e.preventDefault(); e.stopPropagation();
    const m = JSON.parse(JSON.stringify(R.keysMap()));
    const other = learning === 'next' ? 'prev' : 'next';
    m[other] = m[other].filter((k) => k !== id);
    if (!m[learning].includes(id)) m[learning].unshift(id);
    saveSetting('keys', m);
    $('msLearnMsg').textContent = t('learned_' + learning, { key: keyName(id) });
    learning = null;
    renderKeys();
    stage('pedal_learned');
    return true;
};
$('msLearnNext').addEventListener('click', () => { learning = 'next'; $('msLearnMsg').textContent = t('learn_press_next'); });
$('msLearnPrev').addEventListener('click', () => { learning = 'prev'; $('msLearnMsg').textContent = t('learn_press_prev'); });
$('msKeysReset').addEventListener('click', () => { saveSetting('keys', null); renderKeys(); $('msLearnMsg').textContent = t('keys_reset_done'); });
bindSelect('msGap', 'gap', (v) => R.setGap(v), (v) => +v);

/* ── hands-free panel ─────────────────────────────────────────────────── */

els.gKind.value = settings.gKind;
els.gKind.addEventListener('change', () => { saveSetting('gKind', els.gKind.value); G.onKindChange(); syncGestureHelp(); });
els.gSens.value = settings.gSens;
els.gSens.addEventListener('input', () => { saveSetting('gSens', +els.gSens.value); G.rebuild(); });
els.gOn.addEventListener('click', () => { if (G.on) G.disable(); else G.enable(els.gOn); });
els.gCalib.addEventListener('click', () => G.startCalibration());
function syncGestureHelp() {
    document.querySelectorAll('[data-ghelp]').forEach((el) => { el.hidden = el.dataset.ghelp !== settings.gKind; });
}
syncGestureHelp();

document.addEventListener('visibilitychange', () => { if (!document.hidden && R.open) R.relayout(); });

/* ── boot ─────────────────────────────────────────────────────────────── */

(async function boot() {
    applyLook();
    S.store = await openStore();
    if (!S.store.persistent) {
        $('msStoreNote').hidden = false;
        diag('idb_unavailable', S.store.reason || 'none');
    }
    await loadLibrary();
    stage('library', { pieces: pieces.length, setlists: setlists.length });
    // test hook: the e2e reads state, never drives through it
    window.__musicStand = { R, G, S, settings, get pieces() { return pieces; }, get setlists() { return setlists; }, openPiece, openSetlist, DEFAULT_KEYS };
})();
